import React from 'react';
import { Pill } from '@/components/ui/Pill';
import { LazySection } from '@/components/ui/LazySection';
import Link from 'next/link';
import { Cpu, ExternalLink } from 'lucide-react';

interface TechnologyDirectorySectionProps {
  filteredTechnologies: any[];
  viewMode: 'card' | 'list';
  extractFields: (technology: any) => any;
  platformOptions: string[];
  productOptions: { id: string; name: string }[];
  platformLookup: Record<string, string>;
  selectedPlatforms: string[];
  selectedProducts: string[];
  selectedCROs: string[];
  selectedPlexTypes: string[];
  selectedResolution: string[];
  selectedThroughput: string[];
}

export default function TechnologyDirectorySection({
  filteredTechnologies,
  viewMode,
  extractFields,
  platformOptions,
  productOptions,
  platformLookup,
  selectedPlatforms,
  selectedProducts,
  selectedCROs,
  selectedPlexTypes,
  selectedResolution,
  selectedThroughput,
}: TechnologyDirectorySectionProps) {
  if (viewMode === 'list') {
    return (
      <LazySection minHeight={400}>
        <div className="space-y-4">
          {filteredTechnologies.map((technology: any, idx: number) => {
            const { imageSrc, name, description, slug } = extractFields(technology);
            
            return (
              <div key={technology.id || idx} className="bg-white rounded-lg shadow-lg p-6 flex flex-col hover:shadow-xl transition-shadow duration-200">
                <div className="flex items-start gap-4">
                  {/* Image Container */}
                  <div className="flex-shrink-0 w-32 h-32 flex bg-neutral-100 rounded-lg overflow-hidden shadow-md">
                    {imageSrc ? (
                      <img src={imageSrc} alt={`${name} technology`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Cpu className="w-8 h-8 text-neutral-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-display font-bold text-xl text-primary-700 truncate">
                        {slug ? (
                          <Link href={`/technologies/${slug}`} className="hover:text-primary-600 transition-colors">
                            {name}
                          </Link>
                        ) : (
                          name
                        )}
                      </h3>
                    </div>
                    
                    {description && (
                      <p className="text-sm text-neutral-700 mb-3 line-clamp-2">{description}</p>
                    )}
                    
                    {/* Pills for technology-specific fields */}
                    <div className="flex flex-wrap gap-2">
                      {selectedPlatforms.length > 0 && technology['Platforms'] && Array.isArray(technology['Platforms']) && [...new Set(technology['Platforms'].map((platform: any) => {
                        const platformId = typeof platform === 'string' ? platform : (platform.id || platform);
                        return platformLookup[platformId] || platformId;
                      }).filter((platformName: string) => selectedPlatforms.includes(platformName)))].map((platformName: string, i: number) => (
                        <Pill key={i} type="technologyPlatform">{platformName}</Pill>
                      ))}
                      {selectedPlexTypes.length > 0 && technology['Plex Type'] && Array.isArray(technology['Plex Type']) && [...new Set(technology['Plex Type'].filter((plex: string) => selectedPlexTypes.includes(plex)))].map((plex: string, i: number) => (
                        <Pill key={i} type="technologyPlexType">{plex}</Pill>
                      ))}
                      {selectedResolution.length > 0 && technology['Resolution'] && Array.isArray(technology['Resolution']) && [...new Set(technology['Resolution'].filter((res: string) => selectedResolution.includes(res)))].map((res: string, i: number) => (
                        <Pill key={i} type="technologyResolution">{res}</Pill>
                      ))}
                      {selectedThroughput.length > 0 && technology['Throughput'] && Array.isArray(technology['Throughput']) && [...new Set(technology['Throughput'].filter((thru: string) => selectedThroughput.includes(thru)))].map((thru: string, i: number) => (
                        <Pill key={i} type="technologyThroughput">{thru}</Pill>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </LazySection>
    );
  }

  // Card view
  return (
    <LazySection minHeight={400}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTechnologies.map((technology: any, idx: number) => {
          const { imageSrc, name, description, slug } = extractFields(technology);
          
          // If we have a slug, make the entire card clickable
          if (slug) {
            return (
              <Link
                key={technology.id || idx}
                href={`/technologies/${slug}`}
                className="block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
                aria-label={`View details for ${name}`}
              >
                <div className="bg-white rounded-lg shadow-lg p-6 min-h-80 flex flex-col hover:shadow-xl transition-shadow duration-200 cursor-pointer">
                  {/* Image Section */}
                  <div className="w-full mb-4 bg-neutral-100 rounded-lg overflow-hidden shadow-md" style={{ height: 120 }}>
                    {imageSrc ? (
                      <img 
                        src={imageSrc} 
                        alt={`${name} technology`} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Cpu className="w-8 h-8 text-neutral-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Content Section - Flexible but with consistent spacing */}
                  <div className="flex-1 flex flex-col">
                    <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
                      {name}
                    </h3>
                    
                    {description && (
                      <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
                        {description}
                      </p>
                    )}
                    
                    {/* Pills Section - Fixed at bottom */}
                    <div className="mt-auto">
                      <div className="flex flex-wrap gap-2">
                        {selectedPlatforms.length > 0 && technology['Platforms'] && Array.isArray(technology['Platforms']) && [...new Set(technology['Platforms'].map((platform: any) => {
                          const platformId = typeof platform === 'string' ? platform : (platform.id || platform);
                          return platformLookup[platformId] || platformId;
                        }).filter((platformName: string) => selectedPlatforms.includes(platformName)))].slice(0, 2).map((platformName: string, i: number) => (
                          <Pill key={i} type="technologyPlatform">{platformName}</Pill>
                        ))}
                        {selectedPlexTypes.length > 0 && technology['Plex Type'] && Array.isArray(technology['Plex Type']) && [...new Set(technology['Plex Type'].filter((plex: string) => selectedPlexTypes.includes(plex)))].slice(0, 2).map((plex: string, i: number) => (
                          <Pill key={i} type="technologyPlexType">{plex}</Pill>
                        ))}
                        {selectedResolution.length > 0 && technology['Resolution'] && Array.isArray(technology['Resolution']) && [...new Set(technology['Resolution'].filter((res: string) => selectedResolution.includes(res)))].slice(0, 2).map((res: string, i: number) => (
                          <Pill key={i} type="technologyResolution">{res}</Pill>
                        ))}
                        {selectedThroughput.length > 0 && technology['Throughput'] && Array.isArray(technology['Throughput']) && [...new Set(technology['Throughput'].filter((thru: string) => selectedThroughput.includes(thru)))].slice(0, 2).map((thru: string, i: number) => (
                          <Pill key={i} type="technologyThroughput">{thru}</Pill>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          }

          // If no slug, render as a regular div
          return (
            <div key={technology.id || idx} className="bg-white rounded-lg shadow-lg p-6 min-h-80 flex flex-col">
              {/* Image Section */}
              <div className="w-full mb-4 bg-neutral-100 rounded-lg overflow-hidden shadow-md" style={{ height: 120 }}>
                {imageSrc ? (
                  <img 
                    src={imageSrc} 
                    alt={`${name} technology`} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Cpu className="w-8 h-8 text-neutral-400" />
                  </div>
                )}
              </div>
              
              {/* Content Section - Flexible but with consistent spacing */}
              <div className="flex-1 flex flex-col">
                <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
                  {name}
                </h3>
                
                {description && (
                  <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
                    {description}
                  </p>
                )}
                
                {/* Pills Section - Fixed at bottom */}
                <div className="mt-auto">
                  <div className="flex flex-wrap gap-2">
                    {selectedPlatforms.length > 0 && technology['Platforms'] && Array.isArray(technology['Platforms']) && [...new Set(technology['Platforms'].map((platform: any) => {
                      const platformId = typeof platform === 'string' ? platform : (platform.id || platform);
                      return platformLookup[platformId] || platformId;
                    }).filter((platformName: string) => selectedPlatforms.includes(platformName)))].slice(0, 2).map((platformName: string, i: number) => (
                      <Pill key={i} type="technologyPlatform">{platformName}</Pill>
                    ))}
                    {selectedPlexTypes.length > 0 && technology['Plex Type'] && Array.isArray(technology['Plex Type']) && [...new Set(technology['Plex Type'].filter((plex: string) => selectedPlexTypes.includes(plex)))].slice(0, 2).map((plex: string, i: number) => (
                      <Pill key={i} type="technologyPlexType">{plex}</Pill>
                    ))}
                    {selectedResolution.length > 0 && technology['Resolution'] && Array.isArray(technology['Resolution']) && [...new Set(technology['Resolution'].filter((res: string) => selectedResolution.includes(res)))].slice(0, 2).map((res: string, i: number) => (
                      <Pill key={i} type="technologyResolution">{res}</Pill>
                    ))}
                    {selectedThroughput.length > 0 && technology['Throughput'] && Array.isArray(technology['Throughput']) && [...new Set(technology['Throughput'].filter((thru: string) => selectedThroughput.includes(thru)))].slice(0, 2).map((thru: string, i: number) => (
                      <Pill key={i} type="technologyThroughput">{thru}</Pill>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </LazySection>
  );
}